import { useState } from 'react';

import { ArrowRightLeft, ArrowUpDown, CircleCheck, FlipHorizontal2 } from 'lucide-react';

import { InconsistenciesFact } from './InconsistencyFact';
import {
  EvidenceCountIndicator,
  InconsistencyLevelIndicator,
  InconsistencyModeIndicator,
  InconsistencyTypeIndicator,
} from './InconsistencyIndicators';
import {
  InconsistencySource,
  InconsistencyType,
} from '../../../../../../../../backend/src/case/inconsistency/inconsistency-display';
import { TextWithReferences } from '../../../../../../helpers/formatReferences';
import { useUpdateInconsistencyReportItem } from '@/api/mutations/useUpdateInconsistencyReportItem';
import Chip from '@/components/atoms/chip';
import { Button } from '@/components/atoms/new-button';
import Accordion from '@/components/molecules/Accordion';
import EditableField from '@/components/molecules/EditableField';
import { cn } from '@/helpers/shadCnUtils';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { Inconsistency, InconsistencyFact, Relevance } from '@/types';

const InconsistencyRow = ({
  inconsistency,
  caseId,
  openDocComparer,
  compressedVersion,
  isReportInconsistency = false,
  onSourceClick,
  onTypeClick,
}: {
  inconsistency: Inconsistency;
  caseId: string;
  openDocComparer?: (docGroups: { [key: string]: InconsistencyFact[] }) => void;
  compressedVersion?: boolean;
  isReportInconsistency?: boolean;
  onSourceClick?: (source: InconsistencySource) => void;
  onTypeClick?: (type: InconsistencyType) => void;
}) => {
  const { openDocViewer } = useDocumentNavigation();

  const colors = [
    {
      bg: 'bg-blue-50/50',
      line: 'bg-blue-200',
    },
    {
      bg: 'bg-red-50/50',
      line: 'bg-red-200',
    },
    {
      bg: 'bg-purple-50/50',
      line: 'bg-purple-200',
    },
    {
      bg: 'bg-orange-50/50',
      line: 'bg-orange-200',
    },
  ];

  const docGroups: { [key: string]: InconsistencyFact[] } = inconsistency.facts ?? {};
  const groupNames = Object.keys(docGroups);
  const groupCount = groupNames.length;
  const totalFacts = Object.values(docGroups).reduce((acc, group) => acc + group.length, 0);

  // Check if any fact in this inconsistency has relevant facts
  const hasRelevantFacts = Object.values(docGroups).some((group) =>
    group.some((inconsistencyFact) => inconsistencyFact.fact?.relevance === Relevance.YES),
  );

  const [inconsistencyDescription, setInconsistencyDescription] = useState(inconsistency.description);

  const { mutate: updateInconsistencyReportItem } = useUpdateInconsistencyReportItem();

  function handleUpdateInconsistencyDescription(value: string) {
    if (inconsistency.reportId === undefined) {
      return;
    }

    updateInconsistencyReportItem({
      caseId,
      reportId: inconsistency.reportId,
      reportItemId: inconsistency.id,
      body: {
        description: value,
      },
    });
    setInconsistencyDescription(value);
  }

  return (
    <Accordion
      title={
        <div className={cn('mt-3 ml-1 w-full text-sm font-normal', compressedVersion && 'text-xs')}>
          <div className="flex w-full justify-between gap-2">
            <InconsistencyLevelIndicator
              level={inconsistency.level}
              inconsistencyId={inconsistency.id}
              caseId={caseId}
            />
            <div className="flex items-center gap-1.5 text-xs">
              {!compressedVersion && <EvidenceCountIndicator count={totalFacts} />}
              {!compressedVersion && hasRelevantFacts && (
                <Chip className="flex items-center gap-1.5 rounded-md bg-green-100 py-0.75 text-green-700">
                  <CircleCheck size={12} className="text-green-700" />
                  <span className="text-xs font-medium">Contains Relevant Facts</span>
                </Chip>
              )}
              <InconsistencyModeIndicator mode={inconsistency.source} onClick={onSourceClick} />
              <InconsistencyTypeIndicator type={inconsistency.type} onClick={onTypeClick} />
            </div>
          </div>
          <div className="w-full p-1">
            {isReportInconsistency ? (
              <EditableField
                value={inconsistencyDescription}
                onSave={handleUpdateInconsistencyDescription}
                isLoading={false}
              />
            ) : (
              <div className="my-2">
                <TextWithReferences content={inconsistency.description} />
              </div>
            )}
          </div>
        </div>
      }
      content={
        <div className={cn('mb-4 px-2 text-sm', compressedVersion && 'text-xs')}>
          {openDocComparer && !compressedVersion && (
            <Button
              variant="outline"
              size="sm"
              className="text-semibold mt-1 mb-5 flex h-auto items-center gap-2 bg-gray-50 py-1.5 text-gray-700 hover:border-gray-300"
              onClick={() => openDocComparer(docGroups)}
            >
              <FlipHorizontal2 />
              Compare Docs
            </Button>
          )}

          {groupNames.map((groupName: string, index: number) => (
            <div key={groupName}>
              <div className={`relative ml-3 rounded-md p-3`}>
                <p className="mb-2 ml-1 text-sm font-semibold">{groupName}</p>
                <div
                  className={`absolute top-1/2 -left-2.5 h-full w-1 -translate-y-1/2 rounded-full ${colors[index % groupCount].line}`}
                />
                <div className="flex flex-col gap-2">
                  {inconsistency.facts[groupName].map((fact: InconsistencyFact) => (
                    <InconsistenciesFact
                      key={fact.id}
                      fact={fact.fact}
                      openDocViewer={openDocViewer}
                      compressedVersion={compressedVersion}
                    />
                  ))}
                </div>
              </div>
              {index < groupNames.length - 1 && (
                <div className="my-2 flex w-full items-center justify-center">
                  <ArrowUpDown className="my-2 size-4 text-gray-500" />
                </div>
              )}
            </div>
          ))}
        </div>
      }
      className="relative w-full p-0"
    />
  );
};

export default InconsistencyRow;
